import { Agent } from '../types';
import { 
  Brain, 
  Users, 
  Workflow, 
  Shield, 
  MessageSquare, 
  Code, 
  Network, 
  Sparkles, 
  Settings, 
  Wand2, 
  Activity, 
  Bot 
} from 'lucide-react';

// Helper functions to reduce cognitive complexity
const hasSupportKeywords = (name: string, description: string): boolean => {
  return name.includes('support') || description.includes('support') || description.includes('help');
};

const hasAnalysisKeywords = (name: string, description: string): boolean => {
  return name.includes('analyzer') || name.includes('analysis') || description.includes('analyz');
};

const hasWorkflowKeywords = (name: string, description: string): boolean => {
  return name.includes('workflow') || description.includes('workflow') || description.includes('process');
};

const hasSecurityKeywords = (name: string, description: string): boolean => {
  return name.includes('security') || description.includes('security') || name.includes('guard');
};

const hasChatKeywords = (name: string, description: string): boolean => {
  return name.includes('chat') || name.includes('conversation') || description.includes('chat');
};

const hasCodeKeywords = (name: string, description: string): boolean => {
  return name.includes('code') || description.includes('code') || description.includes('programming');
};

const hasNetworkKeywords = (name: string, description: string): boolean => {
  return name.includes('network') || description.includes('network') || name.includes('connect');
};

const hasIntelligentKeywords = (name: string, description: string): boolean => {
  return name.includes('smart') || name.includes('intelligent') || description.includes('ai');
};

const hasManagementKeywords = (name: string, description: string): boolean => {
  return name.includes('manager') || description.includes('manage') || name.includes('admin');
};

const hasCreationKeywords = (name: string, description: string): boolean => {
  return name.includes('creator') || description.includes('create') || name.includes('generate');
};

const hasMonitoringKeywords = (name: string, description: string): boolean => {
  return name.includes('monitor') || description.includes('monitor') || name.includes('watch');
};

const hasAssistantKeywords = (name: string, description: string): boolean => {
  return name.includes('assistant') || description.includes('assist') || name.includes('helper');
};

// Agent icon mapping based on agent name or characteristics with reduced cognitive complexity
export const getAgentIcon = (agent: Agent) => {
  const name = agent.agentName.toLowerCase();
  const description = agent.agentDescription?.toLowerCase() || '';
  
  if (hasSupportKeywords(name, description)) return Users;
  if (hasAnalysisKeywords(name, description)) return Brain;
  if (hasWorkflowKeywords(name, description)) return Workflow;
  if (hasSecurityKeywords(name, description)) return Shield;
  if (hasChatKeywords(name, description)) return MessageSquare;
  if (hasCodeKeywords(name, description)) return Code;
  if (hasNetworkKeywords(name, description)) return Network;
  if (hasIntelligentKeywords(name, description)) return Sparkles;
  if (hasManagementKeywords(name, description)) return Settings;
  if (hasCreationKeywords(name, description)) return Wand2;
  if (hasMonitoringKeywords(name, description)) return Activity;
  if (hasAssistantKeywords(name, description)) return Bot;
  
  // Default icon
  return Brain;
};

// Agent type classification based on tools and description
export const getAgentType = (agent: Agent): string => {
  const toolCount = agent.agentSelectedTools?.length || 0;
  const description = agent.agentDescription?.toLowerCase() || '';
  const name = agent.agentName.toLowerCase();
  
  if (agent.isCore) return 'Core';
  if (toolCount >= 5) return 'Advanced';
  if (name.includes('specialist') || description.includes('specialist')) return 'Specialist';
  if (toolCount >= 3) return 'Standard';
  if (description.includes('simple') || description.includes('basic')) return 'Basic';
  
  return toolCount > 0 ? 'Standard' : 'Basic';
};

// Get agent status color
export const getAgentStatusColor = (type: string): string => {
  switch (type) {
    case 'Core': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Advanced': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Specialist': return 'bg-green-100 text-green-800 border-green-200';
    case 'Standard': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Basic': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Get agent capability score (0-100)
export const getAgentCapabilityScore = (agent: Agent): number => {
  let score = 0;
  
  // Base score for having a description
  if (agent.agentDescription && agent.agentDescription.length > 20) score += 20;
  
  // Score for tools (up to 40 points)
  const toolCount = agent.agentSelectedTools?.length || 0;
  score += Math.min(toolCount * 8, 40);
  
  // Score for tags (up to 20 points)
  const tagCount = agent.agentTags?.length || 0;
  score += Math.min(tagCount * 5, 20);
  
  // Score for prompt quality (up to 20 points)
  if (agent.agentPrompt && agent.agentPrompt.length > 50) score += 20;
  
  return Math.min(score, 100);
};

// Get capability level based on score
export const getCapabilityLevel = (score: number): { level: string; color: string } => {
  if (score >= 80) return { level: 'Expert', color: 'text-green-600' };
  if (score >= 60) return { level: 'Advanced', color: 'text-blue-600' };
  if (score >= 40) return { level: 'Intermediate', color: 'text-orange-600' };
  if (score >= 20) return { level: 'Basic', color: 'text-gray-600' };
  return { level: 'Minimal', color: 'text-red-600' };
};

// Generate gradient classes for agent cards
export const getAgentGradient = (agent: Agent): string => {
  const gradients = [
    'from-blue-50 to-blue-100',
    'from-purple-50 to-purple-100',
    'from-green-50 to-green-100',
    'from-orange-50 to-orange-100',
    'from-pink-50 to-pink-100',
    'from-indigo-50 to-indigo-100',
    'from-teal-50 to-teal-100',
    'from-red-50 to-red-100',
  ];
  
  // Use agent ID to consistently assign gradient
  const hash = agent.agentId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return gradients[Math.abs(hash) % gradients.length];
};

// Format agent creation/update time
export const formatAgentTime = (timestamp?: string): string => {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    
    return `${Math.floor(diffDays / 365)} years ago`;
  } catch {
    return 'Unknown';
  }
};

// Check if user can edit agent (alias for canEditAgent for compatibility)
export const checkEditPermission = (agent: Agent, currentUserId: string, isAdmin: boolean): boolean => {
  return canEditAgent(agent, currentUserId, isAdmin);
};

// Check if user can edit agent
export const canEditAgent = (agent: Agent, currentUserId?: string, isAdmin?: boolean): boolean => {
  if (!currentUserId) return false;
  if (isAdmin) return true;
  if (agent.ownerId === currentUserId) return true;
  
  const { editAccessType, editAllowedUsers } = agent.accessControl || {};
  
  switch (editAccessType) {
    case 'All Users':
      return true;
    case 'Allowed Users':
      return editAllowedUsers?.includes(currentUserId) || false;
    case 'Owner Only':
      return false;
    default:
      return false;
  }
};

// Get agent complexity rating
export const getAgentComplexity = (agent: Agent): { rating: string; description: string } => {
  const toolCount = agent.agentSelectedTools?.length || 0;
  const promptLength = agent.agentPrompt?.length || 0;
  const hasAdvancedConfig = agent.agentTags && agent.agentTags.length > 3;
  
  if (toolCount >= 5 && promptLength > 200 && hasAdvancedConfig) {
    return { rating: 'Complex', description: 'Multi-tool agent with advanced configuration' };
  }
  
  if (toolCount >= 3 && promptLength > 100) {
    return { rating: 'Moderate', description: 'Well-configured agent with multiple capabilities' };
  }
  
  if (toolCount >= 1 || promptLength > 50) {
    return { rating: 'Simple', description: 'Basic agent with essential configuration' };
  }
  
  return { rating: 'Minimal', description: 'Basic setup requiring additional configuration' };
}; 